import React from 'react'
import { Flex, Text } from '@sendoutcards/quantum-design-ui'

import { useSelector } from 'src/hooks'

type DashboardPageLayoutProps = {
  title?: string
  children: React.ReactNode
  enableStickyHeader?: boolean
  disableOuterPadding?: boolean
  disableContentPadding?: boolean
  outerHorizontalPaddingOverride?: number
  outerBottomPaddingOverride?: number
  titleTextMarginLeftOverride?: number
}

const DashboardPageLayout: React.FC<DashboardPageLayoutProps> = ({
  title,
  children,
  enableStickyHeader: _enableStickyHeader = true,
  disableOuterPadding = false,
  disableContentPadding = false,
  outerHorizontalPaddingOverride,
  outerBottomPaddingOverride,
  titleTextMarginLeftOverride,
}) => {
  const isMobile = useSelector(state => state.window.isMobile)
  const layoutCss = {
    '--dashboard-container-padding': '10px',
    '--dashboard-outer-horizontal-padding': '10px',
    '--dashboard-outer-bottom-padding': '10px',
    '--dashboard-title-margin-left': '4px',
    '@media (min-width: 600px)': {
      '--dashboard-container-padding': '12px',
      '--dashboard-outer-horizontal-padding': '10px',
      '--dashboard-outer-bottom-padding': '10px',
    },
    '@media (min-width: 720px)': {
      '--dashboard-container-padding': '16px',
      '--dashboard-outer-horizontal-padding': '12px',
      '--dashboard-outer-bottom-padding': '10px',
    },
    '@media (min-width: 860px)': {
      '--dashboard-container-padding': '20px',
      '--dashboard-outer-horizontal-padding': '16px',
      '--dashboard-outer-bottom-padding': '12px',
    },
    '@media (min-width: 980px)': {
      '--dashboard-container-padding': '24px',
      '--dashboard-outer-horizontal-padding': '20px',
      '--dashboard-outer-bottom-padding': '14px',
    },
    '@media (min-width: 1100px)': {
      '--dashboard-container-padding': '28px',
      '--dashboard-outer-horizontal-padding': '24px',
      '--dashboard-outer-bottom-padding': '16px',
    },
    '@media (min-width: 1246px)': {
      '--dashboard-container-padding': '32px',
      '--dashboard-outer-horizontal-padding': '30px',
      '--dashboard-outer-bottom-padding': '16px',
    },
    '@media (max-width: 400px)': {
      '--dashboard-outer-horizontal-padding': '0px',
      '--dashboard-outer-bottom-padding': '0px',
    },
  }
  const layoutStyleVars = {
    ...(disableOuterPadding
      ? {
          '--dashboard-outer-horizontal-padding': '0px',
          '--dashboard-outer-bottom-padding': '0px',
        }
      : {}),
    ...(disableContentPadding
      ? {
          '--dashboard-container-padding': '0px',
          '--dashboard-title-margin-left': '0px',
        }
      : {}),
    ...(outerHorizontalPaddingOverride != null
      ? {
          '--dashboard-outer-horizontal-padding': `${outerHorizontalPaddingOverride}px`,
        }
      : {}),
    ...(outerBottomPaddingOverride != null
      ? {
          '--dashboard-outer-bottom-padding': `${outerBottomPaddingOverride}px`,
        }
      : {}),
    ...(titleTextMarginLeftOverride != null
      ? {
          '--dashboard-title-margin-left': `${titleTextMarginLeftOverride}px`,
        }
      : {}),
  } as React.CSSProperties

  return (
    <Flex
      width="100%"
      height="100%"
      flexDirection="column"
      css={layoutCss}
      style={{
        padding:
          '0 var(--dashboard-outer-horizontal-padding) var(--dashboard-outer-bottom-padding)',
        minHeight: 0,
        ...layoutStyleVars,
      }}
    >
      <Flex
        flexDirection="column"
        width="100%"
        height="100%"
        borderColor="#fff"
        borderRadius="32px"
        position="relative"
        overflow="hidden"
        style={{
          minHeight: 0,
          flex: 1,
          isolation: 'isolate',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '32px',
            overflow: 'hidden',
            clipPath: 'inset(0 round 32px)',
            WebkitClipPath: 'inset(0 round 32px)',
            transform: 'translateZ(0)',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        >
          <div
            style={{
              position: 'absolute',
              width: '560px',
              height: '560px',
              left: '-180px',
              top: '-220px',
              background:
                'radial-gradient(circle at 35% 35%, rgba(160, 205, 255, 0.55), rgba(160, 205, 255, 0) 70%)',
              filter: 'blur(8px)',
              opacity: 0.85,
            }}
          />
          <div
            style={{
              position: 'absolute',
              width: '520px',
              height: '520px',
              right: '-140px',
              bottom: '-200px',
              background:
                'radial-gradient(circle at 60% 60%, rgba(176, 245, 232, 0.45), rgba(176, 245, 232, 0) 68%)',
              filter: 'blur(12px)',
              opacity: 0.8,
            }}
          />
        </div>
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '32px',
            backgroundColor: '#F8F5F699',
            border: '1px solid rgba(255, 255, 255, 0.4)',
            backdropFilter: 'blur(26px) saturate(140%)',
            WebkitBackdropFilter: 'blur(26px) saturate(140%)',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
        {title ? (
          <Flex
            position="relative"
            zIndex={2}
            backgroundColor="#fff"
            width="100%"
            style={{
              padding: '16px var(--dashboard-container-padding)',
              borderTopLeftRadius: '28px',
              borderTopRightRadius: '28px',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
            }}
          >
            <Text
              type="largeBody"
              weight="bold"
              color="primaryHeading"
              content={title}
              style={{
                letterSpacing: '.3px',
                marginLeft: 'var(--dashboard-title-margin-left)',
              }}
            />
          </Flex>
        ) : null}
        <Flex
          id="dashboard-scroll-container"
          flexDirection="column"
          height="100%"
          css={
            isMobile
              ? {
                  scrollbarWidth: 'none' as const,
                  msOverflowStyle: 'none' as const,
                  '::-webkit-scrollbar': {
                    display: 'none' as const,
                    width: 0,
                    height: 0,
                  },
                }
              : undefined
          }
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: 'var(--dashboard-container-padding)',
            position: 'relative',
            zIndex: 2,
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
          }}
          rowGap="x3"
        >
          {children}
        </Flex>
      </Flex>
    </Flex>
  )
}

export default DashboardPageLayout
